import type {
  BranchByIdResult,
  BranchRow,
  BranchesListResult,
  BranchWithSettingsResult,
  ScheduleUpdatePayload,
} from "@/modules/branches/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type PickupSlotsResult = {
  date: string;
  timezone: string;
  pickupEnabled: boolean;
  asap: {
    available: boolean;
    pickupAt: string | null;
    reason?: string;
  };
  slots: Array<{
    startAt: string;
    endAt: string;
    label: string;
    bookedCount: number;
    capacity: number;
    available: boolean;
    reason?: string;
  }>;
};

export type BranchCreatePayload = {
  code: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  serviceRadiusM?: number | null;
  timezone?: string | null;
  openHoursJson: Record<string, { open: string; close: string } | null>;
  isActive: boolean;
  isDistributionHub?: boolean;
};

export const branchesApi = {
  async list(query: OffsetPageQueryDTO): Promise<BranchesListResult> {
    const url = new URL(`${config.apiUrl}/branches`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<BranchesListResult>(url.toString());
  },

  async get(id: string): Promise<BranchByIdResult> {
    return fetcher.get<BranchByIdResult>(`${config.apiUrl}/branches/${id}`);
  },

  async getWithSettings(id: string): Promise<BranchWithSettingsResult> {
    return fetcher.get<BranchWithSettingsResult>(
      `${config.apiUrl}/branches/${id}/with-settings`,
    );
  },

  async updateSchedule(
    id: string,
    payload: ScheduleUpdatePayload,
  ): Promise<{ id: string }> {
    return fetcher.put<{ id: string }>(
      `${config.apiUrl}/branches/${id}/schedule`,
      payload,
    );
  },

  async getPickupSlots(
    id: string,
    date: string,
  ): Promise<PickupSlotsResult> {
    const url = new URL(`${config.apiUrl}/branches/${id}/pickup-slots`);
    url.searchParams.set("date", date);
    return fetcher.get<PickupSlotsResult>(url.toString());
  },

  async create(payload: BranchCreatePayload): Promise<BranchRow> {
    return fetcher.post<BranchRow>(`${config.apiUrl}/branches`, payload);
  },

  async update(id: string, payload: BranchCreatePayload): Promise<BranchRow> {
    return fetcher.put<BranchRow>(`${config.apiUrl}/branches/${id}`, payload);
  },

  async remove(id: string): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(`${config.apiUrl}/branches/${id}`);
  },
};
